import { createContext, useContext, useEffect, useRef, ReactNode } from "react";
import useLocationSharing, { PrivacyLevel } from "@/hooks/useLocationSharing";
import useAuth, { SUPERUSER_BYPASS_EMAIL, SUPERUSER_BYPASS_ENABLED } from "@/hooks/useAuth";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Geolocation } from "@capacitor/geolocation";

const BACKGROUND_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const BACKGROUNDED_AT_KEY = "giftmap_backgrounded_at";

interface LocationSharingContextType {
  sharing: boolean;
  error: string | null;
  privacyLevel: PrivacyLevel;
  setPrivacyLevel: (level: PrivacyLevel) => void;
  startSharing: () => Promise<boolean>;
  stopSharing: () => void;
  checkInOnce?: (userId: string, level?: PrivacyLevel) => Promise<{ ok: true } | { ok: false; error: string }>;
}

const LocationSharingContext = createContext<LocationSharingContextType | null>(null);

export const LocationSharingProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { start, stop, sharing, error, privacyLevel, setPrivacyLevel, checkInOnce } = useLocationSharing({ 
    intervalMs: 15000 
  });

  const isBypassSuperuser = !!user?.email && SUPERUSER_BYPASS_ENABLED && user.email.toLowerCase() === SUPERUSER_BYPASS_EMAIL;
  const bypassSharingEnabled = (() => {
    if (!isBypassSuperuser) return false;
    try {
      return localStorage.getItem("giftmap_bypass_sharing") === "true";
    } catch {
      return false;
    }
  })();
  
  const backgroundTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasActiveBeforeBackgroundRef = useRef<boolean>(false);

  // Auto-start location sharing when user is authenticated and privacy is not "off"
  useEffect(() => {
    const platform = Capacitor.getPlatform?.() || 'web';
    if (user?.id && privacyLevel !== "off") {
      if (platform !== 'ios') {
        // Auto-start only on non-iOS platforms
        start(user.id, privacyLevel);
      } else {
        // On iOS, proactively request location permission so the system prompt appears
        (async () => {
          try {
            await Geolocation.requestPermissions();
          } catch {}
        })();

        // For bypass users, restore the last local sharing state so the toggle text stays in sync.
        if (isBypassSuperuser && bypassSharingEnabled) {
          start(user.id, privacyLevel);
        }
      }
    } else if (!user?.id) {
      stop();
    }
  }, [user?.id, start, stop, privacyLevel, isBypassSuperuser, bypassSharingEnabled]);

  // Handle app state changes (background/foreground) - native only
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      if (!user?.id) return;

      if (!isActive) {
        // App went to background - store timestamp and start timer
        wasActiveBeforeBackgroundRef.current = sharing;
        localStorage.setItem(BACKGROUNDED_AT_KEY, Date.now().toString());
        
        backgroundTimerRef.current = setTimeout(() => {
          console.log("Background timeout reached - stopping location sharing");
          stop();
        }, BACKGROUND_TIMEOUT_MS);
        
      } else {
        // App returned to foreground - cancel any pending timer
        if (backgroundTimerRef.current) {
          clearTimeout(backgroundTimerRef.current);
          backgroundTimerRef.current = null;
        }
        
        // Check if we were backgrounded too long (handles app kill scenarios)
        const backgroundedAt = localStorage.getItem(BACKGROUNDED_AT_KEY);
        if (backgroundedAt) {
          const elapsed = Date.now() - parseInt(backgroundedAt);
          localStorage.removeItem(BACKGROUNDED_AT_KEY);
          
          if (elapsed > BACKGROUND_TIMEOUT_MS) {
            console.log("Background timeout exceeded on resume - stopping location sharing");
            stop();
            return;
          }
        }
        
        // Restart location sharing if it was active before and privacy allows
        if (wasActiveBeforeBackgroundRef.current && privacyLevel !== "off") {
          const platform = Capacitor.getPlatform?.() || 'web';
          if (platform !== 'ios') {
            start(user.id, privacyLevel);
          }
        }
      }
    };

    App.addListener("appStateChange", handleAppStateChange);

    return () => {
      App.removeAllListeners();
      if (backgroundTimerRef.current) {
        clearTimeout(backgroundTimerRef.current);
      }
    };
  }, [user?.id, sharing, privacyLevel, start, stop]);

  const startSharing = async (): Promise<boolean> => {
    if (user?.id) {
      // On iOS, use checkInOnce first to ensure location is written before returning
      const platform = Capacitor.getPlatform?.() || 'web';
      if (platform === 'ios' && isBypassSuperuser) {
        start(user.id, privacyLevel);
        return true;
      }

      if (platform === 'ios' && checkInOnce) {
        const result = await checkInOnce(user.id, privacyLevel);
        if (result?.ok) {
          start(user.id, privacyLevel);
          return true;
        }
        return false;
      }
      // On other platforms, start normally
      start(user.id, privacyLevel);
      return true;
    }
    return false;
  };

  const stopSharing = () => {
    stop();
  };

  return (
    <LocationSharingContext.Provider value={{
      sharing,
      error,
      privacyLevel,
      setPrivacyLevel,
      startSharing,
      stopSharing,
      checkInOnce,
    }}>
      {children}
    </LocationSharingContext.Provider>
  );
};

export const useLocationSharingContext = () => {
  const context = useContext(LocationSharingContext);
  if (!context) {
    throw new Error("useLocationSharingContext must be used within LocationSharingProvider");
  }
  return context;
};
